/**
 * standDurumuService.js — Stand Durumu Özeti (toplu stand sağlık göstergesi)
 *
 * Kendi mock veri seti yok — standOverviewService.js (proje/taahhütname/denetim
 * ham verisi), standTalepService.js (borç + talep kalemleri) ve
 * firmaProfileService.js (dekoratör firmasının evrak durumu) verilerini stand
 * başına tek bir özet objede birleştirir. FirmaStandOverviewPage.jsx'teki
 * "Stand Durumu" sekmesi ve FirmaAnasayfaListPage.jsx'teki aynı isimli widget
 * bu dosyayı ortak kullanır — hesaplama mantığı iki sayfa arasında kopyalanmasın diye.
 *
 * Beş alt durum:
 *  - proje: İnşaat izni verildi mi VE tüm taahhütnameler onaylı mı.
 *  - borc: standTalepService.getBorcDurumu — hiçbir kalem "bekliyor" değilse tamam
 *    (Alan/Teknik/Ekipman + Hizmet Borcu, bkz. HIZMET_BORC_META).
 *  - evrak: dekoratör firmasının kendi evrakları (Vergi Levhası/İmza Sirküsü/Fuar
 *    evrakları/3 firma belgesi) — firma bazlı, stand bazlı değil; bu yüzden aynı
 *    dekoratörün tüm standlarında aynı değeri taşır.
 *  - talep: Hizmet/İş İzni/Ekipman taleplerinden hiçbiri reddedilmediyse tamam
 *    (Bekliyor durumundaki talepler normal bir ara durum kabul edilir, sorun sayılmaz).
 *  - denetim: üç sabit kategorinin (bkz. DENETIM_KATEGORILERI: İSG/Proje/
 *    BetterStands) HER BİRİNİN en son ziyareti "uygun" olmalı (geçmişteki eski
 *    bir "uygunsuzluk" daha sonra "uygun" bir ziyaretle kapatıldıysa artık
 *    sorun sayılmaz — sadece son kayıt geçerli) VE ayrıca kategorilerden bağımsız
 *    stand başına tek bir bayrak olan `stand.kontrolFormu.gecti` true olmalı.
 *
 * `null` bir alt durum için "veri yok / uygulanamaz" anlamına gelir (ör. proje
 * henüz deklare edilmemiş, ya da dekoratörün firma profili mock verisinde yok) —
 * true/false'tan ayrı tutulur ki genel değerlendirmeyi yanlışlıkla olumlu/olumsuz
 * yönde etkilemesin.
 *
 * `seviye` (genel gösterge):
 *  - 'projeYok': stand.projectDeclared === false — henüz proje bilgisi girilmedi.
 *  - 'sorunlu': admin'in problemliStand bayrağı 'evet' YA DA proje/borç/talepten
 *    biri false.
 *  - 'tamamlandi': sorunlu değil ama evrak/denetim ikisi birden henüz tam değil.
 *  - 'tam': sorunlu değil VE evrak tamam VE denetim tamam (Kontrol Formu dahil) —
 *    en açık yeşil kademe. NOT: evrak durumu dekoratör firması genelinde paylaşılan
 *    bir veri olduğundan, bu kademe sadece o dekoratörün evrakları da tam olduğunda
 *    ulaşılabilir; evrak eksikse üst kademeye çıkılamaz ama alt (sorunlu/tamamlandı)
 *    ayrımını etkilemez (bkz. /revise-page sırasında verilen karar).
 */
import {
  getBorcDurumu, getHizmetTalepleri, getIsIzinleri, getEkipmanTalebi,
} from './standTalepService';
import i18n from "../i18n";

// `seviye` → hex renk — FirmaStandOverviewPage.jsx'teki GRAND_DURUMU_META'nın renk
// kaynağı (label/desc/Icon o dosyada React-yerel kalır, bu servis dosyası React
// import etmez). FirmaStandListPage.jsx (Standlarım liste kartındaki koyu stand
// rozetinin arka planı) ve FirmaAnasayfaListPage.jsx'teki "Stand Durumu" widget'ı
// da aynı sabiti kullanır — üç tüketici arasında renk kayması olmasın diye burada
// merkezi tutulur.
export const SEVIYE_RENK = {
  projeYok: '#878a99',
  sorunlu: '#f06548',
  tamamlandi: '#0ab39c',
  tam: '#75dd93',
};

export function projeDurumuOk(stand) {
  if (stand.projectDeclared === false) return null;
  const izinVar = stand.requiredCount > 0 && stand.approvedCount === stand.requiredCount;
  const commitments = stand.commitments || [];
  const taahhutlerTamam = commitments.length > 0 && commitments.every((c) => c.status === 1);
  return izinVar && taahhutlerTamam;
}

export function borcDurumuOk(borcItems) {
  if (!borcItems || borcItems.length === 0) return null;
  return borcItems.every((i) => i.status !== 'bekliyor');
}

export function evrakDurumuOk({ profile, documents, firmaBelgeleri } = {}) {
  if (!profile) return null;
  const profileOk = profile.vergiLevhasiStatus === 'tamamlandi' && profile.imzaSirkusuStatus === 'tamamlandi';
  const docsOk = (documents || []).every((d) => d.status === 'onayli');
  // `ok` canlı Firma İSG grubundan gelir (#258: işveren yolunda onaysız 88/89 sağlanmış sayılır); mock'ta yok.
  const belgelerOk = Object.values(firmaBelgeleri || {}).every((b) => b.ok ?? b.status === 1);
  return profileOk && docsOk && belgelerOk;
}

export function talepDurumuOk(hizmetItems, isIzinItems, ekipmanItem) {
  const requested = [...(hizmetItems || []), ...(isIzinItems || [])].filter((r) => r.hasRequest);
  if (ekipmanItem?.hasRequest) requested.push(ekipmanItem);
  if (requested.length === 0) return true;
  return requested.every((r) => r.status !== 2);
}

// Denetim Durumu'nun kategori sözleşmesi — Stand Durumu sekmesindeki accordion
// (DenetimDurumuAccordion, FirmaStandOverviewPage.jsx) bu listeyi Teknik
// Talepler'deki "her zaman N kalem" kuralıyla aynı şekilde kullanır: veri
// olsun olmasın üçü de her zaman gösterilir.
export const DENETIM_KATEGORILERI = [
  { key: 'ISG', get label() { return i18n.t("services:standDurumuService.isg"); } },
  { key: 'Proje', get label() { return i18n.t("services:standDurumuService.proje"); } },
  { key: 'BetterStands', label: 'BetterStands' },
];

export function denetimDurumuOk(denetimler, kontrolFormu) {
  if (!denetimler || denetimler.length === 0) return null;
  const byKey = {};
  denetimler.forEach((d) => { byKey[d.kategori] = d; });
  const tumKategorilerGecti = DENETIM_KATEGORILERI.every((cat) => {
    const kayitlar = byKey[cat.key]?.kayitlar || [];
    return kayitlar.length > 0 && kayitlar[kayitlar.length - 1].sonuc === 'uygun';
  });
  return tumKategorilerGecti && kontrolFormu?.gecti === true;
}

/**
 * Bir stand için tam özet — Proje/Borç/Denetim standOverviewService kaydından
 * (parametre olarak geçilen `stand`) türetilir, Borç/Talep kalemleri ve firma
 * evrak durumu ayrıca (standTalepService/firmaProfileService) çekilir.
 * `firmaData`: { profile, documents, firmaBelgeleri } — çağıran sayfa, aynı
 * dekoratörün birden çok standı için bunu tek seferde çekip paylaşmalı (bkz.
 * FirmaAnasayfaListPage.jsx'teki kullanım), her stand için tekrar istenmemeli.
 */
export async function getStandDurumuOzet(stand, firmaData = {}, { forceLive = false } = {}) {
  const [borcItems, hizmetItems, isIzinItems, ekipmanItem] = await Promise.all([
    getBorcDurumu(stand.id, { forceLive }),
    getHizmetTalepleri(stand.id, { forceLive }),
    getIsIzinleri(stand.id, { forceLive }),
    getEkipmanTalebi(stand.id, { forceLive }),
  ]);

  const proje = projeDurumuOk(stand);
  const borc = borcDurumuOk(borcItems);
  const evrak = evrakDurumuOk(firmaData);
  const talep = talepDurumuOk(hizmetItems, isIzinItems, ekipmanItem);
  const denetim = denetimDurumuOk(stand.denetimler, stand.kontrolFormu);

  const problemliFlag = stand.standDurumu?.problemliStand === 'evet';
  const sorunlu = problemliFlag || [proje, borc, talep].some((v) => v === false);

  const seviye = stand.projectDeclared === false
    ? 'projeYok'
    : sorunlu
      ? 'sorunlu'
      : (denetim === true && evrak === true) ? 'tam' : 'tamamlandi';

  return { proje, borc, evrak, talep, denetim, sorunlu, seviye, borcItems, hizmetItems, isIzinItems, ekipmanItem };
}
